import { MainNavbar } from "../components/MainNavbar";
import { Outlet } from "react-router-dom";
import React, { useContext, useState } from "react";
import { Box } from "@mui/material";
import { IntegratedSystem } from "../api/integratedsystems";
import { SideBarWidthContext } from "../App";

/**
 * Represents the properties of the AppLayout component.
 *
 * @typedef {object} AppLayoutProps
 * @property {function} [onLogout] - The callback function to be called when the user logs out.
 * @property {React.Component} [Sidebar] - The component to be rendered as the sidebar.
 * @property {string} [userProfile] - The user's profile.
 * @property {number} selectedSystemId - The ID of the selected system.
 * @property {(number) => void} setSelectedSystemId - The callback function to set the selected system ID.
 * @property {IntegratedSystem[]} userSystems - The array of integrated systems for the user.
 * @property {string} currentUser - The name of the current user.
 */
export interface AppLayoutProps {
  Sidebar?: any;
  userProfile?: string;
  selectedSystemId: number;
  setSelectedSystemId: (number) => void;
  userSystems: IntegratedSystem[];
}

/**
 * Renders the main layout of the application.
 *
 * @param {Readonly<AppLayoutProps>} props - The properties for the AppLayout component.
 * @param {React.ComponentType} props.Sidebar - The sidebar component.
 * @param {object} props.userProfile - The user profile.
 * @param {string} props.selectedSystemId - The id of the currently selected system.
 * @param {Function} props.setSelectedSystemId - The function to be called when the system id is set.
 * @param {Array} props.userSystems - The list of user systems.
 *
 * @return {React.Element} The rendered AppLayout component.
 */
export function AppLayout(props: Readonly<AppLayoutProps>) {
  const {
    Sidebar,
    userProfile,
    selectedSystemId,
    setSelectedSystemId,
    userSystems,
  } = props;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const sideBarWidth = useContext(SideBarWidthContext);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        paddingTop: 59 / 8, // Convert px to theme units
        ...(sideBarWidth.value === "small"
          ? {
              pl: { lg: 5 }, // 40px converted to theme units
            }
          : {
              pl: { lg: 30 }, // 240px converted to theme units
            }),
      }}
    >
      <Sidebar
        onMobileClose={() => setIsMobileNavOpen(false)}
        openMobile={isMobileNavOpen}
        userProfile={userProfile}
        selectedSystemId={selectedSystemId}
        setSelectedSystemId={setSelectedSystemId}
        userSystems={userSystems}
      />
      <div
        style={{
          display: "flex",
          flex: "1 1 auto",
          overflowY: "hidden",
        }}
        id="scroll-container"
      >
        <div style={{ display: "flex", flex: "1 1 auto" }}>
          <div
            style={{
              flex: "1 1 auto",
              height: "100%",
            }}
          >
            <MainNavbar onMobileNavOpen={() => setIsMobileNavOpen(true)} />
            <Outlet />
          </div>
        </div>
      </div>
    </Box>
  );
}
